import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Home, Info, KeyRound } from 'lucide-react';
import type { DemoLoginRequest, Me } from '@vuokramani/shared';
import { Brand } from '../../components/Brand.tsx';
import { LanguageSelect, ThemeSwitch } from '../../components/Topbar.tsx';
import { Alert, Splash } from '../../components/ui.tsx';
import { api, DEMO_READONLY_EVENT, errorKey } from '../../lib/api.ts';
import { homePath, meQueryKey, useLogout, useMe } from '../../lib/auth.ts';
import { markDemoVisit, SURVEY_URL } from '../../lib/demo.ts';
import { Facade } from '../auth/Facade.tsx';
import a from '../auth/auth.module.css';
import s from './demo.module.css';

/** Esittelyversion aloitussivu: kokeile vuokranantajana tai vuokralaisena. */
export function DemoStartPage() {
  const { t } = useTranslation();
  const { data: me, isLoading } = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const login = useMutation({
    mutationFn: (as: DemoLoginRequest['as']) =>
      api<Me>('/auth/demo-login', { method: 'POST', body: { as } }),
    onSuccess: (user, as) => {
      markDemoVisit();
      queryClient.setQueryData(meQueryKey, user);
      void navigate(as === 'tenant' ? '/vuokralainen' : '/vuokranantaja');
    },
  });

  if (isLoading) return <Splash />;
  if (me) return <Navigate to={homePath(me)} replace />;

  const roles = [
    { as: 'landlord' as const, icon: <KeyRound size={20} /> },
    { as: 'tenant' as const, icon: <Home size={20} /> },
  ];

  return (
    <div className={a.page}>
      <div className={a.formSide}>
        <div className={a.formTop}>
          <Brand to="/kirjaudu" />
          <div className={a.formTopRight}>
            <LanguageSelect />
            <ThemeSwitch />
          </div>
        </div>

        <div className={a.formWrap}>
          <div className={a.heading}>
            <h1 className={a.title}>{t('demo.title')}</h1>
            <p className={a.lead}>{t('demo.lead')}</p>
          </div>

          <div className={s.roles}>
            {roles.map((role) => (
              <button
                key={role.as}
                type="button"
                className={s.role}
                disabled={login.isPending}
                onClick={() => login.mutate(role.as)}
              >
                <span className={s.roleIcon} aria-hidden="true">
                  {role.icon}
                </span>
                <span className={s.roleText}>
                  <span className={s.roleTitle}>{t(`demo.${role.as}.title`)}</span>
                  <span className={s.roleDesc}>{t(`demo.${role.as}.text`)}</span>
                </span>
                <ChevronRight size={18} className={s.roleArrow} aria-hidden="true" />
              </button>
            ))}
          </div>
          {login.isError && <Alert>{t(errorKey(login.error))}</Alert>}
          <p className={s.note}>{t('demo.note')}</p>
        </div>
      </div>

      <aside className={a.visual}>
        <Facade />
        <div className={a.visualText}>
          <h2 className={a.visualTitle}>{t('login.panelTitle')}</h2>
          <p className={a.visualBody}>{t('login.panelText')}</p>
        </div>
      </aside>
    </div>
  );
}

/**
 * Palkki sovelluksen yläreunassa: esittelyversio, palaute ja roolin vaihto. Näyttää myös
 * ilmoituksen, kun tallennus hylätään (kaikki tallennukset kulkevat saman virheen kautta).
 */
export function DemoBar() {
  const { t } = useTranslation();
  const logout = useLogout();
  const [notice, setNotice] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const show = () => {
      setNotice(true);
      clearTimeout(timer);
      timer = setTimeout(() => setNotice(false), 4500);
    };
    window.addEventListener(DEMO_READONLY_EVENT, show);
    return () => {
      window.removeEventListener(DEMO_READONLY_EVENT, show);
      clearTimeout(timer);
    };
  }, []);

  return (
    <>
      <div className={s.bar}>
        <span>
          <span className={s.barLabel}>{t('demo.barLabel')}</span> {t('demo.barText')}
        </span>
        <span className={s.barLinks}>
          {SURVEY_URL && (
            <a href={SURVEY_URL} target="_blank" rel="noreferrer">
              {t('demo.survey')}
            </a>
          )}
          <button type="button" onClick={() => logout.mutate()}>
            {t('demo.switchRole')}
          </button>
        </span>
      </div>
      {notice && (
        <div className={s.toast} role="status">
          <Info size={18} aria-hidden="true" style={{ flex: 'none', marginTop: 1 }} />
          <span>
            <strong>{t('demo.readonlyTitle')}</strong>
            {t('demo.readonlyText')}
          </span>
        </div>
      )}
    </>
  );
}
